/**
 * "My Content" — one place where a signed-in user sees everything they've
 * created (causes, help offers, appeals, news submissions, memorial
 * nominations, family trees, articles) with edit / archive / delete
 * actions. Uses the generic /my-content/{type}/{id} endpoint.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Archive, Edit3, Trash2, RotateCcw, ExternalLink } from "lucide-react";

const TYPE_LABELS = {
  causes: "Causes",
  appeals: "Appeals",
  help_offers: "Help offers",
  news_submissions: "News submissions",
  memorial_nominations: "Memorial nominations",
  family_trees: "Family trees",
  sp_articles: "Articles",
  events: "Events",
};

const TYPE_LINKS = {
  causes: (d) => `/causes/${d.slug || d.id}`,
  appeals: (d) => `/offer-help/${d.slug || d.id}`,
  help_offers: (d) => `/find-help/${d.slug || d.id}`,
  family_trees: (d) => `/family/${d.slug || d.id}`,
  sp_articles: (d) => `/articles/${d.slug}`,
  news_submissions: () => null,
  memorial_nominations: () => null,
  events: (d) => `/events/${d.slug || d.id}`,
};

export default function MyContent() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/my-content");
      setItems(data.items);
    } catch (e) { toast.error("Failed to load your content"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  const act = async (item, action) => {
    let note = null;
    if (action === "delete") {
      if (!window.confirm("Delete this permanently?")) return;
      note = window.prompt("Any note about why you're deleting? (optional)") || null;
    } else if (action === "archive") {
      note = window.prompt("Any note about archiving? (optional)") || null;
    }
    try {
      await http.post(`/my-content/${item.content_type}/${item.id}`, { action, note });
      toast.success(`${action} complete`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!user) return (
    <div className="max-w-lg mx-auto p-16 text-center">
      <h1 className="font-display text-3xl">Sign in required.</h1>
      <Link to="/login" className="wofy-btn-primary mt-6 inline-flex">Sign in</Link>
    </div>
  );

  const groups = {};
  for (const it of items) {
    (groups[it.content_type] = groups[it.content_type] || []).push(it);
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-14" data-testid="my-content-page">
      <div className="overline mb-2">MY CONTENT</div>
      <h1 className="font-display text-4xl md:text-5xl leading-tight">Everything you've shared</h1>
      <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>
        Edit, archive or delete any content you created. Archiving hides the item from public views but
        preserves it for you and the moderation team.
      </p>

      {loading ? (
        <p className="mt-8 text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading…</p>
      ) : items.length === 0 ? (
        <div className="wofy-card text-center mt-8 py-12" style={{ color: "var(--wofy-text-soft)" }}>
          You haven't posted anything yet.
        </div>
      ) : (
        <div className="mt-8 space-y-8">
          {Object.entries(groups).map(([type, list]) => (
            <section key={type} data-testid={`mc-section-${type}`}>
              <h2 className="font-display text-2xl mb-3">{TYPE_LABELS[type] || type}</h2>
              <div className="space-y-2">
                {list.map((it) => {
                  const linkFn = TYPE_LINKS[it.content_type];
                  const url = linkFn ? linkFn(it) : null;
                  const archived = it.status === "archived";
                  return (
                    <div
                      key={`${it.content_type}-${it.id}`}
                      data-testid={`mc-item-${it.id}`}
                      className="wofy-card flex items-start gap-3"
                      style={archived ? { opacity: 0.55 } : undefined}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                          {it.created_at ? new Date(it.created_at).toLocaleDateString(undefined, { dateStyle: "medium" }) : ""}
                          {archived && <span className="ml-2 text-[10px] uppercase font-mono tracking-wider" style={{ color: "#B23A3A" }}>ARCHIVED</span>}
                        </div>
                        <div className="font-semibold">{it.title || "(untitled)"}</div>
                        {url && (
                          <Link to={url} className="text-xs underline inline-flex items-center gap-0.5 mt-1" style={{ color: "var(--wofy-primary)" }}>
                            View <ExternalLink className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {url && (
                          <Link to={`${url}/edit`} data-testid={`mc-edit-${it.id}`} className="wofy-btn-ghost text-xs" title="Edit">
                            <Edit3 className="w-3.5 h-3.5" />
                          </Link>
                        )}
                        {archived ? (
                          <button onClick={() => act(it, "restore")} data-testid={`mc-restore-${it.id}`} className="wofy-btn-ghost text-xs" title="Restore">
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <button onClick={() => act(it, "archive")} data-testid={`mc-archive-${it.id}`} className="wofy-btn-ghost text-xs" title="Archive">
                            <Archive className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => act(it, "delete")} data-testid={`mc-delete-${it.id}`} className="wofy-btn-ghost text-xs" title="Delete" style={{ color: "#B23A3A" }}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
